import { useState } from "react";
import axios from "axios";
import BusyButton from "../components/basic/busy-button";
import ConfirmDialog, {
  type ConfirmDialogProps,
} from "../components/basic/confirm-dialog";
import { apiHttpUrl } from "../helpers/api-base";
import { getAuthToken } from "../helpers/auth";
import type { AccountRow } from "../params/types";

const USERNAME_RE = /^[A-Za-z0-9._]{1,30}$/;

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface MigrationUsernameProps {
  accounts?: AccountRow[];
  setError?: (message: string) => void;
}

export default function MigrationUsername({
  accounts = [],
  setError,
}: MigrationUsernameProps) {
  const [account, setAccount] = useState("");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState<Omit<
    ConfirmDialogProps,
    "onCancel"
  > | null>(null);

  const clean = username.trim().replace(/^@/, "");
  const formValid = Boolean(account) && USERNAME_RE.test(clean);

  const change = async () => {
    setBusy(true);
    setMessage("");
    try {
      const res = await axios.post(
        apiHttpUrl("/api/migration/username"),
        { account, username: clean },
        { headers: authHeaders(), validateStatus: () => true, timeout: 0 },
      );
      const data = res.data || {};
      if (data.ok) {
        setMessage(`Username changed to @${data.username || clean}.`);
        setUsername("");
      } else {
        setError?.(data.error || "Unable to change the username.");
      }
    } catch {
      setError?.("Unable to reach the server.");
    } finally {
      setBusy(false);
    }
  };

  const onChange = () => {
    if (!formValid || busy) return;
    setConfirm({
      title: "Change the username?",
      rows: [
        { label: "Account", value: account },
        { label: "New username", value: `@${clean}` },
      ],
      note: "This renames the account on Instagram. The old username becomes available to others.",
      confirmLabel: "Change",
      onConfirm: () => {
        setConfirm(null);
        void change();
      },
    });
  };

  return (
    <div className="ib-migration-view">
      <div className="ib-settings-section ib-migration-card">
        <div className="ib-migration-bar">
          <label className="ib-migration-limit">
            <span>Account</span>
            <select
              className="ib-migration-select"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              disabled={busy}
            >
              <option value="">Select…</option>
              {accounts
                .filter((a) => a.session_status !== "banned")
                .map((a) => (
                  <option key={a.username} value={a.username}>
                    {a.username}
                    {a.ig_username ? ` (@${a.ig_username})` : ""}
                  </option>
                ))}
            </select>
          </label>
          <input
            className="ib-migration-input"
            type="text"
            placeholder="New Instagram username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onChange();
            }}
            disabled={busy}
            spellCheck={false}
            autoCapitalize="off"
          />
          <BusyButton
            variant="jf-btn jf-btn-primary ib-migration-btn"
            label="Change"
            busy={busy}
            busyLabel="Changing…"
            disabled={!formValid}
            onClick={onChange}
          />
        </div>
        {busy && (
          <p className="ib-migration-meta">
            Changing… this opens the account&apos;s browser and can take a
            minute.
          </p>
        )}
        {message && <p className="ib-migration-meta">{message}</p>}
      </div>
      {confirm && (
        <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}
